import { fetchWithAuth } from './api';

/**
 * Solicitações — residents asking for an access credential (facial, tag, controle, biometria).
 *
 * The resident side has existed in the apps for a while; this is the management side, which had no
 * web screen at all: requests arrived and nothing in the operational view listed them. Endpoints
 * are role-gated server-side to admin | supervisor | sindico.
 */

export type SolicitacaoType =
  | 'facial'
  | 'tag'
  | 'controle'
  | 'biometria'
  // Residents no longer remove a morador or a vehicle themselves: they ask, and this queue
  // answers. These three reached the panel as raw keys before they were listed here.
  | 'remover_pessoa'
  | 'remover_veiculo'
  /** Move an existing tag from one of the unit's vehicles to another. */
  | 'transferir_tag'
  /** Move the CAR between two units the same resident holds — see vehicleTransferSummary. */
  | 'transferir_veiculo';
/**
 * `cancelled` is the AUTHOR withdrawing their own pending request from the apps — a terminal state
 * the síndico never sets and cannot undo (reopening would resurrect a request its author pulled).
 */
export type SolicitacaoStatus = 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';

export interface SolicitacaoPerson {
  id: string;
  first_name: string;
  last_name: string;
  identity_photo_url?: string | null;
  profile_picture_url?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface SolicitacaoVehicle {
  id: string;
  plate: string;
  brand?: string | null;
  model?: string | null;
  color?: string | null;
}

export interface SolicitacaoUnit {
  id: string;
  number: string;
  block?: { id: string; name: string };
  street?: { id: string; name: string };
  street_number?: string;
}

export interface Solicitacao {
  id: string;
  customer_id: string;
  person_id: string;
  unit_id?: string;
  type: SolicitacaoType;
  vehicle_id?: string;
  status: SolicitacaoStatus;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  /** Why the resident withdrew it — required of them, so it is never blank when present. */
  cancellation_reason?: string | null;
  cancelled_at?: string | null;
  person?: SolicitacaoPerson;
  vehicle?: SolicitacaoVehicle;
  /** Preloaded by the management endpoint only — the resident's own list is implicitly their unit. */
  unit?: SolicitacaoUnit;
  /** Where a `transferir_veiculo` should move the car TO; absent on every other type. */
  target_unit?: SolicitacaoUnit;
}

export const SOLICITACAO_TYPE_LABELS: Record<SolicitacaoType, string> = {
  facial: 'Reconhecimento facial',
  tag: 'Tag de acesso',
  controle: 'Controle remoto',
  biometria: 'Biometria',
  remover_pessoa: 'Remover pessoa',
  remover_veiculo: 'Remover veículo',
  transferir_tag: 'Transferir tag',
  transferir_veiculo: 'Transferir veículo',
};

/** "101 · Bloco A", or just the number when the condo has no blocks. */
function unitEndLabel(unit?: SolicitacaoUnit): string | null {
  if (!unit) return null;
  const parts = [unit.number, unit.block?.name].filter((part) => part && part.length > 0);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * "101 → 302" — a vehicle transfer, as the queue shows it.
 *
 * The whole decision on this type is WHICH unit the car moves to, so a row naming only the plate
 * would send the síndico to the vehicle's record to find out what they are approving. Null for
 * every other type, and for a transfer with no destination: a dangling arrow says less than
 * nothing.
 */
export function vehicleTransferSummary(item: Solicitacao): string | null {
  if (item.type !== 'transferir_veiculo') return null;
  const destination = unitEndLabel(item.target_unit);
  if (!destination) return null;
  const origin = unitEndLabel(item.unit);
  return origin ? `${origin} → ${destination}` : `→ ${destination}`;
}

export const SOLICITACAO_STATUS_LABELS: Record<SolicitacaoStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
  completed: 'Concluída',
  // Not "Rejeitada": that is this screen answering no. The resident took it back.
  cancelled: 'Cancelada',
};

export const solicitacoesService = {
  /** @param status omit for every request, regardless of status. */
  async listSolicitacoes(customerId: string, status?: SolicitacaoStatus): Promise<Solicitacao[]> {
    const params = status ? `?status=${status}` : '';
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/credential-requests${params}`);
    if (!res.ok) throw new Error(`Failed to fetch solicitações: ${res.status}`);
    const data = await res.json();
    return data.data ?? [];
  },

  async updateStatus(customerId: string, requestId: string, status: SolicitacaoStatus): Promise<void> {
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/credential-requests/${requestId}/status`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      }
    );
    if (!res.ok) throw new Error(`Failed to update solicitação status: ${res.status}`);
  },
};
